import _ from 'lodash/';
import UniversalContainer from '../../universal-container';
import UniversalText from '../../universal-text/universal-text';
import UniversalImage from '../../universal-image/universal-image';
import UniversalButton from '../../universal-button/universal-button';
import { CARDS_PER_ROW_ENUM, CARD_TREATMENT_ENUM } from './constants';

import {
  repeatable,
  boolCheckboxSpec,
  stringSelectSpec,
  stringInputSpec,
  group,
} from '../../../lib/editing-configs';
import getTextColor from '../../../lib/text-color-config';

export default (componentDocument) => {
  const treatmentShadowOptions =
    _.get(componentDocument, 'props.cardTreatment', '') ===
    CARD_TREATMENT_ENUM.BOX_SHADOW
      ? [
          stringInputSpec(
            'shadowColorTreatment',
            'Card Drop Shadow Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
        ]
      : '';
  const treatmentBorderOptions =
    _.get(componentDocument, 'props.cardTreatment', '') ===
    CARD_TREATMENT_ENUM.BORDER
      ? [
          stringInputSpec(
            'borderColorTreatment',
            'Card Border Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
          stringInputSpec(
            'borderSizeTreatment',
            'Card Border Width (in px, eg: "2px" - default is 1px)'
          ),
        ]
      : '';
  const cardsAmountConfig = componentDocument?.props?.desktopCarousel
    ? [
        stringSelectSpec(
          'cardsToPreview',
          'Cards to preview?',
          [
            { label: 'Select an option', value: '' },
            { label: '4', value: CARDS_PER_ROW_ENUM.FOUR },
            { label: '3', value: CARDS_PER_ROW_ENUM.THREE },
            { label: '2', value: CARDS_PER_ROW_ENUM.TWO },
            { label: '1', value: CARDS_PER_ROW_ENUM.ONE },
          ],
          {
            help: 'Defines # of cards displayed in desktop carousel preview',
          }
        ),
        boolCheckboxSpec(
          'wideSizeLayout',
          'Display cards in a wider layout? (desktop/tablet only)'
        ),
      ]
    : [
        stringSelectSpec(
          'cardsPerRow',
          'Cards per Row',
          [
            { label: '4', value: CARDS_PER_ROW_ENUM.FOUR },
            { label: '3', value: CARDS_PER_ROW_ENUM.THREE },
            { label: '2', value: CARDS_PER_ROW_ENUM.TWO },
            { label: '1', value: CARDS_PER_ROW_ENUM.ONE },
          ],
          {
            help: '4 columns layout is only available in desktop, on tablet defaults to 3 columns maximum',
          }
        ),
        boolCheckboxSpec(
          'wideSizeLayout',
          'Display cards in a wider layout? (desktop/tablet only)'
        ),
      ];

  const mobileArrowColorInput = componentDocument?.props
    ?.displayMobileCarouselArrow
    ? [
        stringInputSpec(
          'arrowColor',
          'Mobile Carousel Arrow Color (eg: "#fff" - default is #008255)'
        ),
      ]
    : [];

  return [
    boolCheckboxSpec('disableDefaultProps', 'Disable Default Properties?'),
    boolCheckboxSpec('useArticlesData', 'Use Articles Data?'),
    stringInputSpec(
      'tablePressId',
      'TablePress ID (where the article IDs are stored'
    ),
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      [
        'backgroundColor',
        'backgroundImage',
        'border',
        'margin',
        'padding',
        'hide',
        'jumpLinkId',
      ],
      'container'
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'title'
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'subtitle',
      { text: { textType: 'text' } }
    ),
    group('Carousel Settings', [
      ...cardsAmountConfig,
      boolCheckboxSpec(
        'desktopCarousel',
        'Display cards in carousel? (Desktop)'
      ),
      boolCheckboxSpec('stacked', 'Display cards without carousel? (Mobile)'),
      boolCheckboxSpec(
        'displayMobileCarouselArrow',
        'Display Carousel Arrow in Mobile?'
      ),
      boolCheckboxSpec('arrowsOnTheEdges', 'Arrows on the edges?'),
      group('Carousel Styles', [
        stringSelectSpec(
          'cardTreatment',
          'Select Card Treatment',
          [
            { label: 'None', value: CARD_TREATMENT_ENUM.NONE },
            { label: 'Box Shadow', value: CARD_TREATMENT_ENUM.BOX_SHADOW },
            { label: 'Border', value: CARD_TREATMENT_ENUM.BORDER },
          ],
          {
            help: 'Add a border style for the card',
          }
        ),
        ...treatmentShadowOptions,
        ...treatmentBorderOptions,
        ...mobileArrowColorInput,
        ...getTextColor.getEditingConfig(componentDocument),
        ...getTextColor.getEditingConfig(
          componentDocument,
          'carouselDotColorOptions',
          'Carousel dot color',
          'Carousel dot hex color override'
        ),
        ...getTextColor.getEditingConfig(
          componentDocument,
          'carouselInactiveDotColorOptions',
          'Carousel inactive dot color',
          'Carousel inactive dot hex color override'
        ),
        ...getTextColor.getEditingConfig(
          componentDocument,
          'carouselArrowBackgroundOptions',
          'Carousel arrow background color',
          'Carousel arrow hex background color override'
        ),
        ...getTextColor.getEditingConfig(
          componentDocument,
          'carouselArrowColorOptions',
          'Carousel arrow color',
          'Carousel arrow hex color override'
        ),
      ]),
    ]),
    group('Cards', [
      repeatable('cards', '', '', (i) => {
        const shouldShowContainer = _.get(
          componentDocument,
          `props.cards.${i}.$unique_key`,
          false
        );
        // if we don't make this check, it will create a "ghost" card causing bug
        // where we exclude the the card but it remains in the editor
        const containerEditorProps = shouldShowContainer
          ? [
              ...UniversalContainer.getEditingConfig(
                componentDocument,
                'Container',
                [
                  'backgroundColor',
                  'backgroundImage',
                  'border',
                  'margin',
                  'padding',
                  'width',
                ],
                `cards.${i}.flexCardItemContainer`
              ),
            ]
          : [];

        return [
          ...containerEditorProps,
          group('Alignment', [
            stringSelectSpec(`cards.${i}.alignment`, 'Vertical Alignment', [
              { label: 'Top', value: 'flex-start' },
              { label: 'Center', value: 'center' },
              { label: 'Bottom', value: 'flex-end' },
            ]),
            boolCheckboxSpec(
              `cards.${i}.invertImageOutput`,
              'Invert image/text output?'
            ),
          ]),
          ...UniversalText.getEditingConfig(
            componentDocument,
            'Eyebrow',
            [
              'fontWeight',
              'groupButton',
              'margin',
              'padding',
              'fontFamily',
              'textColor',
              'textSize',
              'textType',
            ],
            `cards.${i}.eyebrow`,
            { text: { textType: 'accent' } }
          ),
          ...UniversalText.getEditingConfig(
            componentDocument,
            'Title',
            [
              'fontWeight',
              'groupButton',
              'margin',
              'padding',
              'fontFamily',
              'textColor',
              'textSize',
              'textType',
            ],
            `cards.${i}.title`
          ),
          ...UniversalText.getEditingConfig(
            componentDocument,
            'Subtitle',
            [
              'fontWeight',
              'groupButton',
              'margin',
              'padding',
              'fontFamily',
              'textColor',
              'textSize',
              'textType',
            ],
            `cards.${i}.subtitle`,
            { text: { textType: 'text' } }
          ),
          ...UniversalText.getEditingConfig(
            componentDocument,
            'Card Label',
            [
              'fontWeight',
              'groupButton',
              'margin',
              'padding',
              'fontFamily',
              'textColor',
              'textSize',
              'textType',
            ],
            `cards.${i}.cardLabel`,
            { text: { textType: 'text' } }
          ),
          ...UniversalText.getEditingConfig(
            componentDocument,
            'Body Copy',
            [
              'fontWeight',
              'groupButton',
              'margin',
              'padding',
              'fontFamily',
              'textColor',
              'textSize',
              'textType',
            ],
            `cards.${i}.bodyCopy`
          ),
          ...UniversalImage.getEditingConfig(
            componentDocument,
            'Card Image',
            ['border', 'padding', 'margin'],
            `cards.${i}.cardImage`
          ),
          ...UniversalButton.getEditingConfig(
            componentDocument,
            `cards.${i}.cardButton`,
            'Card Button',
            ['alignment', 'displayStyle']
          ),
        ];
      }),
    ]),
  ];
};
